import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Scissors, Eye, EyeSlash } from "@phosphor-icons/react";
import { API, saveToken } from "@/lib/api";
import { useLocale } from "@/i18n";
import FlagToggle from "@/components/FlagToggle";

export default function LoginPage() {
  const navigate = useNavigate();
  const { t, setLocale } = useLocale();
  const { lang } = useParams();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (lang === "py") setLocale("es");
    else if (lang === "br") setLocale("pt");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (lang && !["br", "py"].includes(lang)) return <Navigate to="/entrar" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { identifier, password });
      saveToken(res.data.token);
      const role = res.data.user.role;
      if (res.data.user.must_change_password) return navigate(lang === "py" ? "/py/trocar-senha" : "/trocar-senha");
      navigate(role === "superadmin" ? "/admin" : role === "partner" ? "/socio" : "/painel");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("login_error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 px-4 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="w-full max-w-md mx-auto px-4 min-h-screen flex flex-col justify-center" data-testid="login-page">
        <div className="flex items-center justify-between mb-2">
          <Link to="/" className="flex items-center gap-2 text-amber-500">
            <Scissors size={20} weight="duotone" />
            <span className="font-display font-bold">Barber Menu</span>
          </Link>
          <FlagToggle />
        </div>
        <h1 className="text-3xl font-black tracking-tight">{t("login_title")}</h1>
        <p className="text-sm text-zinc-500 mt-2">{t("login_sub")}</p>

        <form onSubmit={handleLogin} className="mt-8 space-y-4" data-testid="login-form">
          <input
            data-testid="login-email-input"
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder={t("login_id_ph")}
            autoComplete="username"
            className={inputCls}
          />
          <div className="relative">
            <input
              data-testid="login-password-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("password_ph")}
              autoComplete="current-password"
              className={`${inputCls} pr-12`}
            />
            <button
              type="button"
              data-testid="login-toggle-password-btn"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-500 transition-colors"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <EyeSlash size={20} weight="duotone" /> : <Eye size={20} weight="duotone" />}
            </button>
          </div>
          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-lg bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
          >
            {loading ? t("logging_in") : t("login_title")}
          </button>
        </form>

        <p className="text-sm text-zinc-500 mt-6 text-center">
          {t("no_account")}{" "}
          <Link to="/cadastro" data-testid="go-register-link" className="text-amber-500 font-semibold hover:underline">
            {t("create_page_link")}
          </Link>
        </p>
      </div>
    </div>
  );
}
